import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Banknote,
  Check,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  UserCog,
  X,
} from "lucide-react";

import type { AdminPanelUser } from "../types/admin-panel";
import {
  actualizarConcepto,
  actualizarNomina,
  confirmarNomina,
  crearConcepto,
  descargarNominaPdf,
  eliminarConcepto,
  eliminarNomina,
  generarNominas,
  generarResumenIa,
  getDatosLaborales,
  guardarDatosLaborales,
  listarNominas,
  type DatosLaborales,
  type Nomina,
  type NominaConcepto,
} from "../api/nominas";

type Props = { users: AdminPanelUser[] };

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const eur = (n: string | number | null | undefined) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(
    Number(n ?? 0),
  );

export function AutoPayrollPanel({ users }: Props) {
  const [tab, setTab] = useState<"generar" | "datos">("generar");
  const empleados = useMemo(
    () => users.slice().sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [users],
  );

  return (
    <div className="scroll-reveal-right space-y-5">
      <div className="flex gap-2">
        <TabBtn active={tab === "generar"} onClick={() => setTab("generar")} icon={<Banknote size={15} />}>
          Generar nóminas
        </TabBtn>
        <TabBtn active={tab === "datos"} onClick={() => setTab("datos")} icon={<UserCog size={15} />}>
          Datos laborales y conceptos
        </TabBtn>
      </div>

      {tab === "generar" ? <GenerarTab empleados={empleados} /> : <DatosTab empleados={empleados} />}
    </div>
  );
}

/* ─────────────────────────── Generar nóminas ─────────────────────────── */

function GenerarTab({ empleados }: { empleados: AdminPanelUser[] }) {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [nominas, setNominas] = useState<Nomina[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [detalle, setDetalle] = useState<Nomina | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setNominas(await listarNominas(mes, anio));
    } catch {
      setError("No se pudieron cargar las nóminas.");
    } finally {
      setLoading(false);
    }
  }, [mes, anio]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const generar = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await generarNominas({ mes, anio });
      await cargar();
      if (res.generadas === 0) {
        setError(
          "No se generó ninguna nómina. Asigna conceptos salariales a los empleados en la pestaña «Datos laborales y conceptos».",
        );
      }
    } catch {
      setError("Error al generar las nóminas.");
    } finally {
      setGenerating(false);
    }
  };

  const nombreEmpleado = (n: Nomina) =>
    n.usuario?.nombre ?? empleados.find((e) => e.id === n.usuario_id)?.nombre ?? `#${n.usuario_id}`;

  return (
    <>
      <section className="rounded-[28px] border border-[#e7ecef] bg-white/90 p-6 shadow-[0_14px_45px_rgba(15,23,42,.05)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-emerald-700">
              <Sparkles size={14} /> Nóminas automáticas
            </div>
            <h3 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
              Generar nóminas del periodo
            </h3>
            <p className="mt-1 max-w-xl text-sm font-semibold text-slate-500">
              Calcula un borrador por empleado a partir de su contrato, conceptos
              salariales y fichajes del mes. Revísalo y confírmalo antes de entregarlo.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">Mes</span>
              <select className="field-control" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">Año</span>
              <input
                type="number"
                className="field-control w-[110px]"
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
              />
            </label>
            <button
              onClick={generar}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_14px_32px_rgba(16,185,129,.20)] transition hover:-translate-y-0.5 disabled:opacity-50"
            >
              {generating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Generar / recalcular
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
            {error}
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-[28px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.05)]">
        <div className="border-b border-[#edf1f3] px-5 py-4">
          <h3 className="text-lg font-black text-slate-950">
            Nóminas de {MONTHS[mes - 1]} {anio}
          </h3>
        </div>

        {loading ? (
          <div className="p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="mb-3 h-12 animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : nominas.length === 0 ? (
          <div className="grid min-h-[200px] place-items-center p-8 text-center">
            <div>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
                <Banknote size={22} />
              </div>
              <p className="mt-4 text-sm font-black text-slate-950">Sin nóminas para este periodo.</p>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                Pulsa «Generar / recalcular» para crear los borradores.
              </p>
            </div>
          </div>
        ) : (
          <div className="max-h-[560px] overflow-auto">
            <table className="table-modern table-dark min-w-[920px]">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Empleado</th>
                  <th className="text-right">Días</th>
                  <th className="text-right">Devengado</th>
                  <th className="text-right">Deducciones</th>
                  <th className="text-right">Líquido</th>
                  <th>Estado</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {nominas.map((n) => (
                  <tr key={n.id} className="group">
                    <td className="font-black text-slate-950">{nombreEmpleado(n)}</td>
                    <td className="text-right font-semibold text-slate-600">{Number(n.dias_trabajados)}</td>
                    <td className="text-right font-semibold text-slate-600">{eur(n.total_devengo)}</td>
                    <td className="text-right font-semibold text-rose-600">−{eur(n.total_deduccion)}</td>
                    <td className="text-right font-black text-emerald-700">{eur(n.liquido)}</td>
                    <td><EstadoBadge estado={n.estado} /></td>
                    <td>
                      <div className="flex justify-end gap-2">
                        <IconBtn title="Ver / editar" onClick={() => setDetalle(n)}>
                          <UserCog size={15} />
                        </IconBtn>
                        <IconBtn title="Descargar PDF" onClick={() => void descargarNominaPdf(n.id)}>
                          <Download size={15} />
                        </IconBtn>
                        {n.estado !== "confirmada" && (
                          <IconBtn
                            title="Eliminar"
                            tone="danger"
                            onClick={async () => {
                              if (!confirm("¿Eliminar este borrador?")) return;
                              await eliminarNomina(n.id);
                              await cargar();
                            }}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {detalle && (
        <NominaDetalle
          nomina={detalle}
          nombre={nombreEmpleado(detalle)}
          onClose={() => setDetalle(null)}
          onChanged={async () => {
            await cargar();
          }}
        />
      )}
    </>
  );
}

/* ─────────────────────────── Detalle / edición ─────────────────────────── */

function NominaDetalle({
  nomina,
  nombre,
  onClose,
  onChanged,
}: {
  nomina: Nomina;
  nombre: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [obs, setObs] = useState(nomina.observaciones_ia ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const confirmada = nomina.estado === "confirmada";

  const resumenIa = async () => {
    setBusy("ia");
    try {
      const res = await generarResumenIa(nomina.id);
      if (res.ok && res.observaciones_ia) setObs(res.observaciones_ia);
      else if (res.message) alert(res.message);
    } finally {
      setBusy(null);
    }
  };

  const guardarObs = async () => {
    setBusy("save");
    try {
      await actualizarNomina(nomina.id, { observaciones_ia: obs });
      await onChanged();
    } finally {
      setBusy(null);
    }
  };

  const confirmar = async () => {
    if (!confirm("Confirmar deja la nómina como oficial y no editable. ¿Continuar?")) return;
    setBusy("confirm");
    try {
      await confirmarNomina(nomina.id);
      await onChanged();
      onClose();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="max-h-[88vh] w-full max-w-3xl overflow-auto rounded-[28px] border border-[#e7ecef] bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-xl font-black text-slate-950">{nombre}</h3>
            <p className="text-sm font-semibold text-slate-500">
              {MONTHS[nomina.mes - 1]} {nomina.año} · {Number(nomina.dias_trabajados)} días trabajados
            </p>
          </div>
          <div className="flex items-center gap-2">
            <EstadoBadge estado={nomina.estado} />
            <IconBtn title="Cerrar" onClick={onClose}><X size={16} /></IconBtn>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <LineasCard titulo="Devengos" lineas={nomina.devengos ?? []} total={nomina.total_devengo} tone="emerald" />
          <LineasCard titulo="Deducciones" lineas={nomina.deducciones ?? []} total={nomina.total_deduccion} tone="rose" />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Base CC" value={eur(nomina.base_cc)} />
          <Stat label="Base IRPF" value={eur(nomina.base_irpf)} />
          <Stat label={`IRPF ${Number(nomina.irpf_porcentaje)}%`} value={eur(nomina.irpf_importe)} />
          <Stat label="Líquido" value={eur(nomina.liquido)} highlight />
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
              Observaciones (IA)
            </span>
            <button
              onClick={resumenIa}
              disabled={busy === "ia"}
              className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
            >
              {busy === "ia" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Generar con IA
            </button>
          </div>
          <textarea
            className="field-control min-h-[90px] w-full"
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            placeholder="Notas, incidencias o resumen de la nómina…"
          />
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-[#edf1f3] pt-4">
          <button
            onClick={() => void descargarNominaPdf(nomina.id)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50"
          >
            <Download size={15} /> PDF
          </button>
          {!confirmada && (
            <button
              onClick={guardarObs}
              disabled={busy === "save"}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Guardar notas
            </button>
          )}
          {!confirmada && (
            <button
              onClick={confirmar}
              disabled={busy === "confirm"}
              className="inline-flex items-center gap-2 rounded-xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-4 py-2.5 text-sm font-black text-white shadow-[0_10px_24px_rgba(16,185,129,.20)] transition hover:-translate-y-0.5 disabled:opacity-50"
            >
              {busy === "confirm" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Confirmar nómina
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Datos laborales ─────────────────────────── */

function DatosTab({ empleados }: { empleados: AdminPanelUser[] }) {
  const [usuarioId, setUsuarioId] = useState<number | "">("");
  const [datos, setDatos] = useState<DatosLaborales | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const cargar = useCallback(async (id: number) => {
    setLoading(true);
    try {
      setDatos(await getDatosLaborales(id));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof usuarioId === "number") void cargar(usuarioId);
    else setDatos(null);
  }, [usuarioId, cargar]);

  const setField = <K extends keyof DatosLaborales>(k: K, v: DatosLaborales[K]) =>
    setDatos((d) => (d ? { ...d, [k]: v } : d));

  const guardar = async () => {
    if (!datos || typeof usuarioId !== "number") return;
    setSaving(true);
    try {
      await guardarDatosLaborales(usuarioId, {
        dni: datos.dni,
        naf: datos.naf,
        categoria: datos.categoria,
        grupo_cotizacion: datos.grupo_cotizacion,
        num_pagas: datos.num_pagas,
        irpf_porcentaje: datos.irpf_porcentaje,
        salario_bruto_anual: datos.salario_bruto_anual,
        horas_semanales: datos.horas_semanales,
        fecha_inicio_contratacion: datos.fecha_inicio_contratacion,
        fecha_fin_contrato: datos.fecha_fin_contrato,
        tipo_contrato: datos.tipo_contrato,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-[28px] border border-[#e7ecef] bg-white/90 p-6 shadow-[0_14px_45px_rgba(15,23,42,.05)]">
      <label className="block max-w-md">
        <span className="mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">Empleado</span>
        <select
          className="field-control"
          value={usuarioId}
          onChange={(e) => setUsuarioId(e.target.value ? Number(e.target.value) : "")}
        >
          <option value="">Selecciona un empleado…</option>
          {empleados.map((u) => (
            <option key={u.id} value={u.id}>{u.nombre}</option>
          ))}
        </select>
      </label>

      {loading && (
        <div className="mt-6 grid place-items-center py-10">
          <Loader2 className="animate-spin text-emerald-600" />
        </div>
      )}

      {datos && !loading && (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Input label="DNI / NIE" value={datos.dni ?? ""} onChange={(v) => setField("dni", v)} />
            <Input label="NAF (afiliación SS)" value={datos.naf ?? ""} onChange={(v) => setField("naf", v)} />
            <Input label="Categoría profesional" value={datos.categoria ?? ""} onChange={(v) => setField("categoria", v)} />
            <Input
              label="Grupo de cotización"
              type="number"
              value={datos.grupo_cotizacion ?? ""}
              onChange={(v) => setField("grupo_cotizacion", v === "" ? null : Number(v))}
            />
            <Input
              label="Nº de pagas"
              type="number"
              value={datos.num_pagas ?? ""}
              onChange={(v) => setField("num_pagas", v === "" ? null : Number(v))}
            />
            <Input
              label="Retención IRPF (%)"
              type="number"
              value={datos.irpf_porcentaje ?? ""}
              onChange={(v) => setField("irpf_porcentaje", v === "" ? null : Number(v))}
            />
            <Input
              label="Salario bruto anual (€)"
              type="number"
              value={datos.salario_bruto_anual ?? ""}
              onChange={(v) => setField("salario_bruto_anual", v === "" ? null : Number(v))}
            />
            <Input
              label="Horas semanales"
              type="number"
              value={datos.horas_semanales ?? ""}
              onChange={(v) => setField("horas_semanales", v === "" ? null : Number(v))}
            />
            <Input
              label="Inicio contratación"
              type="date"
              value={datos.fecha_inicio_contratacion ?? ""}
              onChange={(v) => setField("fecha_inicio_contratacion", v || null)}
            />
            <Input
              label="Fin de contrato"
              type="date"
              value={datos.fecha_fin_contrato ?? ""}
              onChange={(v) => setField("fecha_fin_contrato", v || null)}
            />
            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">Tipo de contrato</span>
              <select
                className="field-control w-full"
                value={datos.tipo_contrato ?? ""}
                onChange={(e) => setField("tipo_contrato", e.target.value || null)}
              >
                <option value="">—</option>
                <option value="indefinido">Indefinido</option>
                <option value="temporal">Temporal</option>
                <option value="practicas">Prácticas</option>
                <option value="obra">Obra y servicio</option>
              </select>
            </label>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={guardar}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-2.5 text-sm font-black text-white shadow-[0_10px_24px_rgba(16,185,129,.20)] transition hover:-translate-y-0.5 disabled:opacity-50"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Guardar datos
            </button>
            {savedFlash && <span className="text-sm font-bold text-emerald-600">✓ Guardado</span>}
          </div>

          <ConceptosEditor
            usuarioId={datos.id}
            conceptos={datos.conceptos}
            onReload={() => void cargar(datos.id)}
          />
        </>
      )}
    </section>
  );
}

/* ─────────────────────────── Conceptos CRUD ─────────────────────────── */

function ConceptosEditor({
  usuarioId,
  conceptos,
  onReload,
}: {
  usuarioId: number;
  conceptos: NominaConcepto[];
  onReload: () => void;
}) {
  const [descripcion, setDescripcion] = useState("");
  const [tipo, setTipo] = useState<"devengo" | "deduccion">("devengo");
  const [importe, setImporte] = useState("");
  const [sujeto, setSujeto] = useState(true);
  const [especie, setEspecie] = useState(false);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!descripcion.trim() || importe === "") return;
    setBusy(true);
    try {
      await crearConcepto(usuarioId, {
        descripcion: descripcion.trim(),
        tipo,
        importe: Number(importe),
        sujeto_cotizacion: tipo === "devengo" ? sujeto : false,
        en_especie: tipo === "devengo" ? especie : false,
      });
      setDescripcion("");
      setImporte("");
      onReload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 border-t border-[#edf1f3] pt-6">
      <h4 className="text-lg font-black text-slate-950">Conceptos salariales</h4>
      <p className="mt-1 text-sm font-semibold text-slate-500">
        Define los devengos (salario base, complementos, especie…) y deducciones fijas.
        El motor los usa para calcular la nómina.
      </p>

      <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_140px_120px_auto]">
        <input
          className="field-control"
          placeholder="Descripción (ej. Salario base)"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
        />
        <select className="field-control" value={tipo} onChange={(e) => setTipo(e.target.value as "devengo" | "deduccion")}>
          <option value="devengo">Devengo</option>
          <option value="deduccion">Deducción</option>
        </select>
        <input
          className="field-control"
          type="number"
          step="0.01"
          placeholder="Importe €"
          value={importe}
          onChange={(e) => setImporte(e.target.value)}
        />
        <button
          onClick={add}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-black text-white transition hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Añadir
        </button>
      </div>

      {tipo === "devengo" && (
        <div className="mt-2 flex flex-wrap gap-4 text-sm font-semibold text-slate-600">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={sujeto} onChange={(e) => setSujeto(e.target.checked)} />
            Sujeto a cotización
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={especie} onChange={(e) => setEspecie(e.target.checked)} />
            Retribución en especie
          </label>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-2xl border border-[#e7ecef]">
        <table className="table-modern table-dark w-full">
          <thead>
            <tr>
              <th>Descripción</th>
              <th>Tipo</th>
              <th className="text-right">Importe</th>
              <th>Flags</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {conceptos.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-sm font-semibold text-slate-400">Sin conceptos.</td></tr>
            ) : (
              conceptos.map((c) => (
                <tr key={c.id}>
                  <td className="font-bold text-slate-800">{c.descripcion}</td>
                  <td>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-black ${c.tipo === "devengo" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
                      {c.tipo === "devengo" ? "Devengo" : "Deducción"}
                    </span>
                  </td>
                  <td className="text-right font-semibold text-slate-600">{eur(c.importe)}</td>
                  <td className="text-xs font-semibold text-slate-500">
                    {c.tipo === "devengo" && (
                      <>
                        {c.sujeto_cotizacion ? "Cotiza" : "No cotiza"}
                        {c.en_especie ? " · Especie" : ""}
                      </>
                    )}
                  </td>
                  <td>
                    <div className="flex justify-end gap-2">
                      <IconBtn
                        title={c.activo ? "Desactivar" : "Activar"}
                        onClick={async () => {
                          await actualizarConcepto(c.id, {
                            descripcion: c.descripcion,
                            tipo: c.tipo,
                            importe: Number(c.importe),
                            sujeto_cotizacion: c.sujeto_cotizacion,
                            en_especie: c.en_especie,
                            activo: !c.activo,
                          });
                          onReload();
                        }}
                      >
                        {c.activo ? <Check size={15} /> : <X size={15} />}
                      </IconBtn>
                      <IconBtn
                        title="Eliminar"
                        tone="danger"
                        onClick={async () => {
                          if (!confirm("¿Eliminar concepto?")) return;
                          await eliminarConcepto(c.id);
                          onReload();
                        }}
                      >
                        <Trash2 size={15} />
                      </IconBtn>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────── UI helpers ─────────────────────────── */

function TabBtn({
  active, onClick, icon, children,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-black transition ${
        active
          ? "bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_10px_24px_rgba(16,185,129,.20)]"
          : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function IconBtn({
  children, onClick, title, tone = "default",
}: { children: React.ReactNode; onClick: () => void; title: string; tone?: "default" | "danger" }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`grid h-9 w-9 place-items-center rounded-full border transition hover:-translate-y-0.5 ${
        tone === "danger"
          ? "border-rose-100 bg-rose-50 text-rose-500 hover:bg-rose-100"
          : "border-slate-200 bg-white text-slate-500 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
      }`}
    >
      {children}
    </button>
  );
}

function EstadoBadge({ estado }: { estado: string }) {
  const confirmada = estado === "confirmada";
  return (
    <span className={`inline-flex rounded-full px-3 py-1.5 text-xs font-black ${
      confirmada ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
    }`}>
      {confirmada ? "Confirmada" : "Borrador"}
    </span>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${highlight ? "border-emerald-200 bg-emerald-50" : "border-[#e7ecef] bg-white"}`}>
      <div className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className={`mt-1 text-lg font-black ${highlight ? "text-emerald-700" : "text-slate-950"}`}>{value}</div>
    </div>
  );
}

function LineasCard({
  titulo, lineas, total, tone,
}: { titulo: string; lineas: { descripcion: string; importe: number }[]; total: string | number; tone: "emerald" | "rose" }) {
  return (
    <div className="rounded-2xl border border-[#e7ecef] bg-white p-4">
      <div className="mb-2 text-xs font-black uppercase tracking-[0.12em] text-slate-400">{titulo}</div>
      <div className="space-y-1.5">
        {lineas.length === 0 ? (
          <div className="text-sm font-semibold text-slate-400">—</div>
        ) : (
          lineas.map((l, i) => (
            <div key={i} className="flex justify-between gap-3 text-sm">
              <span className="font-semibold text-slate-600">{l.descripcion}</span>
              <span className="font-bold text-slate-800">{eur(l.importe)}</span>
            </div>
          ))
        )}
      </div>
      <div className={`mt-3 flex justify-between border-t border-dashed border-slate-200 pt-2 text-sm font-black ${tone === "rose" ? "text-rose-600" : "text-emerald-700"}`}>
        <span>Total</span>
        <span>{eur(total)}</span>
      </div>
    </div>
  );
}

function Input({
  label, value, onChange, type = "text",
}: { label: string; value: string | number; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">{label}</span>
      <input className="field-control w-full" type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
